import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Player from "@vimeo/player";
import "./style.css";
import { enrollment, service, certificate } from "../../store";
import { ensureCertificateIssued } from "../../utils/certificate";
// import { debounce } from 'lodash';

// 정상 재생 중 timeupdate 간격(약 0.25초)보다 크면 넘기기로 판단하는 허용 오차(초)
const SEEK_FORWARD_TOLERANCE = 2;

export const VideoPlayer = ({
  videoUrl,
  enrollmentData,
  lectureId,
  course_id,
  onVideoComplete,
  onProgressUpdated,
  useCustomChrome = false,
  restrictSeek = false,
  onSeekBlocked,
  onMaxWatchedTimeUpdate,
  onPlayerReady,
  onPlaybackTimeUpdate,
  onPlayStateChange,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const watchedIntervalsRef = useRef([]);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  // saveProgress 동시 호출 방지용 mutex (state는 비동기라 useRef 사용)
  const isSavingProgressRef = useRef(false);
  // initializePlayer 이펙트가 handleVideoEnded 변경마다 재실행되지 않도록 ref로 분리
  const handleVideoEndedRef = useRef(null);
  // 앞으로 넘기기 허용 최대 지점 — 정상 재생으로 도달한 최대 시간만 반영
  const maxAllowedSeekRef = useRef(0);
  // restrictSeek 변경이 initializePlayer 이펙트를 재실행하지 않도록 ref로 분리
  const restrictSeekRef = useRef(restrictSeek);
  const onSeekBlockedRef = useRef(onSeekBlocked);

  useEffect(() => {
    restrictSeekRef.current = restrictSeek;
  }, [restrictSeek]);

  useEffect(() => {
    onSeekBlockedRef.current = onSeekBlocked;
  }, [onSeekBlocked]);

  const {
    createEnrollmentProgress,
    updateEnrollmentProgress,
    getEnrollmentProgress,
    updateEnrollmentCompletedCount,
    updateEnrollmentIsCompleted,
    updateEnrollmentTotalProcess,
    getEnrollment,
  } = enrollment((state) => ({
    createEnrollmentProgress: state.createEnrollmentProgress,
    updateEnrollmentProgress: state.updateEnrollmentProgress,
    getEnrollmentProgress: state.getEnrollmentProgress,
    updateEnrollmentCompletedCount: state.updateEnrollmentCompletedCount,
    updateEnrollmentIsCompleted: state.updateEnrollmentIsCompleted,
    updateEnrollmentTotalProcess: state.updateEnrollmentTotalProcess,
    getEnrollment: state.getEnrollment,
  }));
  const { getService } = service((state) => ({ getService: state.getService }));
  const { createCertificate, checkCertificate } = certificate((state) => ({
    createCertificate: state.createCertificate,
    checkCertificate: state.checkCertificate,
  }));

  const videoId = useMemo(() => {
    if (!videoUrl) return null;
    const id = videoUrl.split("/").pop().split("?")[0];
    return id && id.length > 0 ? id : null;
  }, [videoUrl]);

  const calculateTotalWatchedTime = useCallback(() => {
    return watchedIntervalsRef.current.reduce((total, interval) => {
      return total + (interval.end - interval.start);
    }, 0);
  }, []);

  const intervalsStorageKey = useMemo(
    () => (lectureId ? `watchedIntervals-${lectureId}` : null),
    [lectureId]
  );

  const persistWatchedIntervals = useCallback(() => {
    if (!intervalsStorageKey) return;
    try {
      localStorage.setItem(
        intervalsStorageKey,
        JSON.stringify(watchedIntervalsRef.current)
      );
    } catch {
      /* ignore */
    }
  }, [intervalsStorageKey]);

  // VideoPlayer.jsx에서 saveProgress 함수의 수정 부분
  const saveProgress = useCallback(async () => {
    if (isSavingProgressRef.current) {
      console.log("saveProgress: 이미 진행 중, 중복 호출 무시");
      return;
    }
    isSavingProgressRef.current = true;
    console.log("saveProgress 함수 시작");
    const totalWatchedTime = calculateTotalWatchedTime();
    const currentIsCompleted = totalWatchedTime / duration >= 0.9;

    if (!enrollmentData?.id) {
      console.error("Enrollment ID is undefined");
      isSavingProgressRef.current = false;
      return;
    }

    try {
      console.log("기존 진도 조회 중...");
      const existingProgress = await getEnrollmentProgress(
        Number(enrollmentData.id)
      );
      console.log("기존 진도:", existingProgress);

      const progressArray = Array.isArray(existingProgress)
        ? existingProgress
        : [];
      const existingLectureProgress = progressArray.find(
        (p) => Number(p.lecture_id) === Number(lectureId)
      );

      const progressData = {
        enrollment_id: Number(enrollmentData.id),
        lecture_id: Number(lectureId),
        video_progress_time: Math.round(Math.min(totalWatchedTime, duration)),
        is_completed: currentIsCompleted,
      };

      let isNewlyCompleted = false;
      const wasAlreadyCompleted = Boolean(existingLectureProgress?.is_completed);

      if (!existingLectureProgress || !existingLectureProgress.is_completed) {
        if (currentIsCompleted) {
          console.log("새로운 진도 생성 중...");
          const result = await createEnrollmentProgress(
            enrollmentData.id,
            progressData
          );
          console.log("새로운 진도 생성 결과:", result);
          isNewlyCompleted = true;
          if (intervalsStorageKey) {
            try {
              localStorage.removeItem(intervalsStorageKey);
            } catch {
              /* ignore */
            }
          }
        }
      } else {
        console.log("이미 완료된 강의입니다. 업데이트 생략.");
      }

      // 마지막 강의 시청 직후 beforeunload로 창이 닫히는 등, 이전 시도에서 수료 처리/
      // 인증서 발급까지 이어지지 못한 경우를 이 강의를 다시 열 때마다 복구할 수 있도록
      // "새로 완료된 경우"뿐 아니라 "이미 완료된 강의를 다시 확인하는 경우"에도 재확인한다.
      if (isNewlyCompleted || wasAlreadyCompleted) {
        console.log("수강 완료 수 및 전체 진도 업데이트 중...");
        const courseData = await getService(course_id);
        console.log("강좌 데이터:", courseData);

        if (courseData) {
          try {
            console.log("getEnrollment 호출, enrollmentId:", enrollmentData.id);
            const currentEnrollment = await getEnrollment(enrollmentData.id);
            console.log("현재 enrollment 정보:", currentEnrollment);

            if (currentEnrollment) {
              // enrollment에 저장된 completed_lecture_count를 그대로 믿고 +1 하면
              // 과거 오차가 누적되어 드리프트될 수 있다. 방금 조회한 실제 진도
              // 기록(progressArray)에서 완료 수를 다시 세어 매번 정확한 값으로 갱신한다.
              // 이번 호출에서 새로 완료된 경우에만 +1 — 이미 완료된 강의는 progressArray에
              // 이미 포함되어 있으므로 중복으로 더하지 않는다.
              const newCompletedCount = isNewlyCompleted
                ? progressArray.filter((p) => p.is_completed).length + 1
                : progressArray.filter((p) => p.is_completed).length;

              console.log("updateEnrollmentCompletedCount 호출 전");
              const updatedCompletedCount =
                await updateEnrollmentCompletedCount(
                  enrollmentData.id,
                  newCompletedCount
                );
              console.log(
                "updateEnrollmentCompletedCount 호출 후, 결과:",
                updatedCompletedCount
              );

              const completedCount =
                updatedCompletedCount?.completed_lecture_count ||
                newCompletedCount;
              console.log("업데이트된 수강 완료 수:", completedCount);

              const totalLectureCount =
                Number(courseData.total_lecture_count) || 1;
              let processRate = Math.round(
                (completedCount / totalLectureCount) * 100
              );
              console.log("계산된 진행률:", processRate);

              // 모든 강의를 완료했는지 확인
              if (completedCount >= totalLectureCount) {
                // 강의를 모두 완료했다면 진도율을 100%로 강제 설정
                processRate = 100;
                console.log("모든 강의 완료. 진도율을 100%로 설정");
              }

              console.log(
                "updateEnrollmentTotalProcess 호출 전, processRate:",
                processRate
              );
              const updateResult = await updateEnrollmentTotalProcess(
                enrollmentData.id,
                processRate
              );
              console.log(
                "updateEnrollmentTotalProcess 호출 후, 결과:",
                updateResult
              );

              if (completedCount >= totalLectureCount) {
                // 이미 완료 처리된 수강은 is-completed를 다시 PATCH하지 않는다.
                // 재호출 시 백엔드가 completed_at(교육 수료일)을 현재 시각으로
                // 갱신해, 이미 발급된 수료증의 발급일보다 나중 날짜가 되는
                // 역전 문제가 생기기 때문. (미완료 상태의 복구 케이스는 그대로 유지)
                if (!currentEnrollment.is_completed) {
                  console.log(
                    "모든 강의 완료. updateEnrollmentIsCompleted 호출 전"
                  );
                  const completionResult = await updateEnrollmentIsCompleted(
                    enrollmentData.id
                  );
                  console.log(
                    "updateEnrollmentIsCompleted 호출 후, 결과:",
                    completionResult
                  );
                }

                const userId = enrollmentData.user_id;
                const courseId = enrollmentData.course_id;

                try {
                  const created = await ensureCertificateIssued({
                    userId,
                    courseId,
                    userName: enrollmentData.user_name,
                    checkCertificate,
                    createCertificate,
                  });
                  console.log(
                    created
                      ? "새로운 이수증서가 발급되었습니다."
                      : "회원의 해당 강의 이수증서가 이미 발급된 상태입니다."
                  );
                } catch (error) {
                  console.error("이수증서 처리 중 오류 발생:", error);
                }
              }
            } else {
              console.error("현재 enrollment 정보를 가져오는데 실패했습니다.");
            }
          } catch (error) {
            console.error("getEnrollment 호출 중 오류 발생:", error);
          }
        }
      }
      console.log("진도 저장 완료");
      await onProgressUpdated?.();
    } catch (error) {
      console.error("진도 저장 중 오류 발생:", error);
    } finally {
      isSavingProgressRef.current = false;
    }
  }, [
    enrollmentData?.id,
    lectureId,
    duration,
    calculateTotalWatchedTime,
    getEnrollmentProgress,
    createEnrollmentProgress,
    updateEnrollmentCompletedCount,
    getService,
    course_id,
    updateEnrollmentIsCompleted,
    createCertificate,
    checkCertificate,
    updateEnrollmentTotalProcess,
    getEnrollment,
    onProgressUpdated,
    intervalsStorageKey,
  ]);

  const updateWatchedIntervals = useCallback((newTime, persist = false) => {
    const intervals = watchedIntervalsRef.current;
    const lastInterval = intervals[intervals.length - 1];

    if (intervals.length === 0 || newTime > lastInterval.end + 1) {
      intervals.push({ start: newTime, end: newTime });
    } else if (
      newTime >= lastInterval.start &&
      newTime <= lastInterval.end + 1
    ) {
      lastInterval.end = Math.max(lastInterval.end, newTime);
    } else {
      let inserted = false;
      for (let i = 0; i < intervals.length; i++) {
        if (newTime < intervals[i].start - 1) {
          intervals.splice(i, 0, { start: newTime, end: newTime });
          inserted = true;
          break;
        } else if (
          newTime >= intervals[i].start - 1 &&
          newTime <= intervals[i].end + 1
        ) {
          intervals[i].start = Math.min(intervals[i].start, newTime);
          intervals[i].end = Math.max(intervals[i].end, newTime);
          inserted = true;
          break;
        }
      }
      if (!inserted) {
        intervals.push({ start: newTime, end: newTime });
      }
    }

    watchedIntervalsRef.current = intervals.reduce((merged, current) => {
      if (!merged.length || current.start > merged[merged.length - 1].end + 1) {
        merged.push(current);
      } else {
        merged[merged.length - 1].end = Math.max(
          merged[merged.length - 1].end,
          current.end
        );
      }
      return merged;
    }, []);

    if (persist) {
      persistWatchedIntervals();
    }
  }, [persistWatchedIntervals]);

  const handleVideoEnded = useCallback(async () => {
    console.log("비디오 종료. 진도 저장 및 완료 모달 표시");
    setIsSaving(true);
    await saveProgress();
    setIsSaving(false);
    console.log("모달 표시 직전");
    onVideoComplete(); // 부모 컴포넌트에 비디오 완료 알림
    console.log("모달 표시 직후");
  }, [saveProgress, onVideoComplete]);

  // ref를 항상 최신 handleVideoEnded로 유지 (initializePlayer 이펙트 재실행 방지)
  useEffect(() => {
    handleVideoEndedRef.current = handleVideoEnded;
  }, [handleVideoEnded]);


  useEffect(() => {
    const initializePlayer = async () => {
      if (
        !videoId ||
        !iframeRef.current ||
        playerRef.current ||
        !enrollmentData?.id
      )
        return;

      try {
        const player = new Player(iframeRef.current, {
          id: videoId,
          width: "100%",
          height: "100%",
          autopause: false,
          autoplay: false,
          loop: false,
          controls: !useCustomChrome,
          responsive: true,
        });

        playerRef.current = player;

        await player.ready();
        const videoDuration = await player.getDuration();
        setDuration(videoDuration);

        if (intervalsStorageKey) {
          try {
            const saved = localStorage.getItem(intervalsStorageKey);
            if (saved) {
              const parsed = JSON.parse(saved);
              if (Array.isArray(parsed)) {
                watchedIntervalsRef.current = parsed.filter(
                  (it) =>
                    it &&
                    Number.isFinite(it.start) &&
                    Number.isFinite(it.end) &&
                    it.end <= videoDuration + 1
                );
              }
            }
          } catch {
            /* ignore */
          }
        }

        // 이전 시청 기록이 있으면 그 최대 지점까지는 넘기기 허용
        maxAllowedSeekRef.current = watchedIntervalsRef.current.reduce(
          (max, interval) => Math.max(max, interval.end),
          0
        );
        onMaxWatchedTimeUpdate?.(maxAllowedSeekRef.current);

        // 시청하지 않은 구간으로의 이동을 허용 지점으로 되돌림.
        // Vimeo iframe 내부 키보드 시크 등 커스텀 컨트롤 밖의 조작도 여기서 차단된다.
        const enforceSeekLimit = (seconds) => {
          if (!restrictSeekRef.current) return false;
          if (seconds <= maxAllowedSeekRef.current + SEEK_FORWARD_TOLERANCE) {
            return false;
          }
          player
            .setCurrentTime(maxAllowedSeekRef.current)
            .catch(() => {/* ignore */});
          onSeekBlockedRef.current?.();
          return true;
        };

        let lastPersistAt = 0;
        const handleTimeUpdate = (data) => {
          if (enforceSeekLimit(data.seconds)) return;
          setCurrentTime(data.seconds);
          const shouldPersist = data.seconds - lastPersistAt >= 5;
          updateWatchedIntervals(data.seconds, shouldPersist);
          if (shouldPersist) lastPersistAt = data.seconds;
          if (data.seconds > maxAllowedSeekRef.current) {
            maxAllowedSeekRef.current = data.seconds;
            onMaxWatchedTimeUpdate?.(data.seconds);
          }
          onPlaybackTimeUpdate?.(data.seconds, videoDuration);
        };

        player.on("timeupdate", handleTimeUpdate);
        player.on("seeked", (data) => enforceSeekLimit(data.seconds));
        player.on("play", () => onPlayStateChange?.(false));
        player.on("pause", () => onPlayStateChange?.(true));
        player.on("ended", () => {
          console.log("Vimeo 'ended' event triggered");
          handleVideoEndedRef.current?.();
        });
        player.on("loaded", () => setIsLoading(false));

        onPlaybackTimeUpdate?.(0, videoDuration);
        onPlayerReady?.(player);

        const savedTime = localStorage.getItem(`watchedTime-${lectureId}`);
        if (savedTime) {
          const parsedTime = parseFloat(savedTime);
          if (parsedTime < videoDuration) {
            await player.setCurrentTime(parsedTime);
          } else {
            localStorage.removeItem(`watchedTime-${lectureId}`);
          }
        }
      } catch (error) {
        console.error("Error initializing Vimeo player:", error);
        setIsLoading(false);
      }
    };

    initializePlayer();

    return () => {
      persistWatchedIntervals();
      if (playerRef.current) {
        playerRef.current.off("timeupdate");
        playerRef.current.off("seeked");
        playerRef.current.off("play");
        playerRef.current.off("pause");
        playerRef.current.off("ended");
        playerRef.current.off("loaded");
        playerRef.current
          .destroy()
          .catch((error) =>
            console.error("Error destroying Vimeo player:", error)
          );
      }
      playerRef.current = null;
    };
  }, [
    videoId,
    lectureId,
    enrollmentData?.id,
    useCustomChrome,
    onPlayerReady,
    onPlaybackTimeUpdate,
    onPlayStateChange,
    onMaxWatchedTimeUpdate,
    intervalsStorageKey,
    persistWatchedIntervals,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      saveProgress();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveProgress]);

  if (!videoUrl) {
    return (
      <div>영상이 정상적으로 처리되지 않았습니다. 관리자에게 문의해주세요.</div>
    );
  }

  return (
    <div
      className={`video-player-container${
        useCustomChrome ? " video-player-container--lecture" : ""
      }`}
      style={
        useCustomChrome
          ? undefined
          : { width: "100%", maxWidth: "1150px", margin: "0 auto" }
      }
    >
      {isLoading && (
        <div className="video-player-loading">
          <div className="video-player-loader"></div>
        </div>
      )}
      <div ref={iframeRef} style={{ aspectRatio: "16 / 9" }}></div>
    </div>
  );
};
